import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';
import SocialLinks from './SocialLinks';
import useBrandingStore from '../store/brandingStore';

/**
 * The branded left panel shown alongside auth forms (Login/Register).
 * Deliberately a flat deep panel with a dot-grid "graph paper" texture and a
 * couple of tilted lesson-card shapes, rather than a gradient hero — keeps a
 * study-app identity without falling back on the generic SaaS gradient look.
 */
export default function BrandPanel() {
  const { t } = useTranslation();
  const { socialLinks, load } = useBrandingStore();
  useEffect(() => { load(); }, [load]);

  return (
    <div className="hidden lg:flex lg:w-[46%] relative bg-primary-900 text-white flex-col justify-between overflow-hidden px-12 py-14">
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.16) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
        }}
      />

      <Link to="/" className="relative inline-block w-fit">
        <Logo size={38} wordmarkClassName="text-lg" />
      </Link>

      <div className="relative flex-1 flex items-center">
        <div className="relative w-full max-w-sm h-64">
          <div className="absolute left-2 top-10 w-56 h-32 rounded-xl bg-primary-700/70 border border-primary-500/40 shadow-xl -rotate-6" />
          <div className="absolute left-16 top-24 w-56 h-32 rounded-xl bg-accent-400 text-primary-900 shadow-xl rotate-3 p-5 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide opacity-70">{t('Progress')}</span>
              <span className="text-xs font-bold">86%</span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-primary-900/15 overflow-hidden">
              <div className="h-full w-[86%] rounded-full bg-primary-900" />
            </div>
            <span className="text-sm font-semibold font-display">{t('Algebra · Chapter 4')}</span>
          </div>
        </div>
      </div>

      <div className="relative">
        <h1 className="text-3xl font-display font-bold leading-tight tracking-tight">
          <span className="text-accent-300">{t('HeroBrandName')}</span>{' '}
          {t('brings everyone together in one learning journey.')}
        </h1>
        <p className="mt-3 text-primary-200 text-sm max-w-xs leading-relaxed">
          {t('Teachers build courses, students learn at their pace, parents follow along, admins keep it all running.')}
        </p>
        <SocialLinks links={socialLinks} className="mt-5" />
      </div>
    </div>
  );
}
